import posthog from 'posthog-js';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';

export function initPostHog() {
  if (!POSTHOG_KEY) {
    console.warn('[posthog] VITE_POSTHOG_KEY not set — analytics disabled');
    return;
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    autocapture: true,
    capture_pageview: true,
    capture_pageleave: true,
    persistence: 'localStorage+cookie',
    // Session replay — watch real user sessions to understand drop-off
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: false,
      maskInputFn: (text, element) => {
        // Mask password fields, leave everything else visible
        if (element?.getAttribute('type') === 'password') return '*'.repeat(text.length);
        return text;
      },
    },
  });
}

// Person-property flags so PostHog dashboards can filter out our own testing.
// Add a new internal address by appending to INTERNAL_EMAILS; add a partner by
// appending to PARTNER_DOMAINS. Any @pontis.life address is internal by default,
// so team members on that domain don't need an explicit entry.
const INTERNAL_EMAILS = new Set([
  'blake.pereira2018@gmail.com',
  'blakespereira@gmail.com',
  'blake@plaquemakerplus.com',
  'blake@pontis.life',
]);
const INTERNAL_DOMAINS = new Set(['pontis.life']);
const PARTNER_DOMAINS = new Set(['walkermonument.com']);

export function computeInternalFlags(email?: string): { is_internal: boolean; is_partner_staff: boolean } {
  if (!email) return { is_internal: false, is_partner_staff: false };
  const normalized = email.trim().toLowerCase();
  const domain = normalized.split('@')[1] ?? '';
  return {
    is_internal: INTERNAL_EMAILS.has(normalized) || INTERNAL_DOMAINS.has(domain),
    is_partner_staff: PARTNER_DOMAINS.has(domain),
  };
}

export function identifyUser(user: { id: string; email?: string; phone?: string }) {
  if (!POSTHOG_KEY) return;
  posthog.identify(user.id, { email: user.email, phone: user.phone, ...computeInternalFlags(user.email) });
}

export function setUserPersonProperties(props: Record<string, unknown>) {
  if (!POSTHOG_KEY) return;
  posthog.setPersonProperties(props);
}

export function resetUser() {
  if (!POSTHOG_KEY) return;
  posthog.reset();
}

/** Capture an event reliably across page unload, redirects, or tab close. Safe to use anywhere — not just immediately before navigation. */
export function captureBeforeNavigate(event: string, properties?: Record<string, unknown>) {
  if (!POSTHOG_KEY) return;
  posthog.capture(event, properties, { send_instantly: true, transport: 'sendBeacon' });
}

export { posthog };
